import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { EntityConfig, downloadBlob } from "./types";
import { clientService, EclientStatus } from "@/services/client";

interface ClientExportPanelProps {
  entity: EntityConfig;
}

export function ClientExportPanel({ entity }: ClientExportPanelProps) {
  const [status, setStatus] = useState<string>("ALL");
  const [search, setSearch] = useState("");
  const [notify, setNotify] = useState<string>("ALL");

  const exportMutation = useMutation({
    mutationFn: () =>
      clientService.exportClients({
        status: status !== "ALL" ? (status as EclientStatus) : undefined,
        search: search || undefined,
        notify: notify !== "ALL" ? notify === "true" : undefined,
      }),
    onSuccess: ({ blob, fileName }) => {
      downloadBlob(blob, fileName);
      toast.success("Exportación generada correctamente.");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor={`export-search-${entity.id}`}>Buscar</Label>
          <Input
            id={`export-search-${entity.id}`}
            placeholder="Nombre, teléfono o email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`export-status-${entity.id}`}>Estado</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id={`export-status-${entity.id}`}>
              <SelectValue placeholder="Todos los estados" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos los estados</SelectItem>
              {entity.statusOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`export-notify-${entity.id}`}>Notificaciones</Label>
          <Select value={notify} onValueChange={setNotify}>
            <SelectTrigger id={`export-notify-${entity.id}`}>
              <SelectValue placeholder="Todas las notificaciones" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas las notificaciones</SelectItem>
              <SelectItem value="true">Notificaciones activas</SelectItem>
              <SelectItem value="false">Notificaciones inactivas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end pt-1">
        <Button onClick={() => exportMutation.mutate()} disabled={exportMutation.isPending}>
          {exportMutation.isPending ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-1.5" />
          )}
          Generar exportación
        </Button>
      </div>
    </div>
  );
}
